import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { useKeyModificationDialogStore } from 'components/WorkspaceEditor/components/KeyModificationWarningDialog'
import { DesignationField } from 'components/WorkspaceEditor/features/drawingDesignation'
import { shouldHideElement } from 'components/WorkspaceEditor/features/drawingDesignation/utils/elementVisibility'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { ADDITIONAL_ELEMENT_SUFFIX } from 'components/WorkspaceEditor/utils'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { preserveTrailingSpaceWithoutNbsp } from '../../ContentEditor/contentUtils'
import { EmptyPlainTextContainer } from '../EmptyPlainTextContainer'
import { EditorContent } from './EditorContent'
import { KeyedText } from './KeyedText'
import { usePlainTextController } from './hooks/usePlainTextController'
import { usePlainTextStyles } from './usePlainTextStyles'

// Utility functions
const generateSuggestionDataAttribute = (suggestionId?: string) => {
    return suggestionId ? `content-suggestion-id-${suggestionId}` : undefined
}

/**
 * Shows the PT key modification warning dialog.
 * Used when user clicks on a non-editable PT key portion.
 */
const showPTKeyWarning = (keyText: string) => {
    const { showDialog, closeDialog } = useKeyModificationDialogStore.getState()
    showDialog({
        isPTKey: true,
        originalKey: keyText,
        modifiedKey: keyText,
        onProceed: closeDialog,
        onCancel: closeDialog,
    })
}

/** Props for the PlainText component. */
export interface PlainTextProps {
    /** The contentItem object that holds the text property that needs to render */
    contentItem: Partial<TextContentElement>
    /** The parent node, used for Product Type detection and key-portion analysis */
    node?: DataNode
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** The classes to apply the styling */
    classes?: string
}

/**
 * An editable component that renders plain text. It uses Typography (caption variant) from Material-UI as a placeholder for CKEditor (ContentEditor).
 * Can be editable based on:
 * - by default, the component is not editable
 * - when the component has changes (initial suggestions)
 * - when focused or clicked
 * @param {PlainTextProps} props - Props for the component.
 */
export const PlainText = memo<PlainTextProps>(
    ({ contentItem, node, inactive = false, editable = false, classes }) => {
        /** controller props and handlers */
        const {
            text,
            keyedText,
            keyedTextValue,
            formattedContentId,
            initialData,
            initialSuggestions,
            clickCoordinates,
            ckeToggle,
            shouldRenderContentEditor,
            isKeyedText,
            hasDesignationFormat,
            hasValueTextAfterFieldRef,
            handlers,
        } = usePlainTextController(contentItem, node)

        const plainTextStyles = usePlainTextStyles()
        const containerRef = useRef<HTMLSpanElement>(null)
        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

        // Prevent CKEditor from trimming trailing spaces
        const cleanedInitialData = useMemo(
            () => preserveTrailingSpaceWithoutNbsp(initialData),
            [initialData]
        )
        // Allow editing inactive content in OM workspace
        const canEditInactive = isCurrentlyOnOMWorkspace || !inactive
        const isEditMode =
            shouldRenderContentEditor && canEditInactive && editable
        const showEmptyPlainTextContainer =
            isKeyedText && !keyedTextValue && !inactive && !isEditMode
        const displayText = text ?? ''
        const hasEmptyText = !text?.trim()

        // Check if this is the first content item in a PT (Product Type name/key portion)
        // PT names can be split across multiple content items where the first item
        // is the key (e.g., "Cementitious Patching Mortar") without a colon
        const isPTFirstItem = Boolean(
            node?.isProductType &&
                contentItem.id === node?.contentItemNodes?.[0]?.id
        )

        // Check if this is the first content item in an Article
        const isArticle = node?.type === 'Article'
        const isArticleFirstItem = Boolean(
            isArticle &&
                !node?.isUserAdded &&
                contentItem.id === node?.contentItemNodes?.[0]?.id
        )

        // Only the FIRST element should render the DesignationField to avoid duplication
        // Applies to Product Types and Articles WITH product type icon (isProductType=true)
        // Articles don't require isKeyedText (no colon in article format)
        // Regular articles without product type icon should NOT show DesignationField
        const isArticleWithProductType = isArticle && node?.isProductType
        const isEditDrawingDesignationMode =
            node?.isProductType &&
            (isKeyedText || isArticleWithProductType || hasDesignationFormat) &&
            contentItem.id &&
            node?.key &&
            (isPTFirstItem || (isArticleWithProductType && isArticleFirstItem)) // Only first element renders designation

        // PT key = first content item only (Bug 2574397: prevents duplicate DesignationField)
        const isPTKey = Boolean(node?.isProductType && isPTFirstItem)

        // Memoize container class to prevent recreation on each render
        const containerClass = useMemo(() => {
            if (!editable) return plainTextStyles.plainTextContainerReadOnly
            if (hasEmptyText) return plainTextStyles.plainTextContainer
            return plainTextStyles.plainTextContainerWithContent
        }, [editable, hasEmptyText, plainTextStyles])

        // Generate suggestion data attribute
        const suggestionDataAttribute = useMemo(
            () => generateSuggestionDataAttribute(initialSuggestions?.[0]?.id),
            [initialSuggestions]
        )

        // Focus handler that blocks activation for locked keys when not in edit mode
        const handleFocusCapture = useCallback(
            (event?: React.FocusEvent<HTMLElement>) => {
                // For designation mode, don't activate on focus - let the click handler decide
                // This prevents the editor from activating before the click handler can show the PT key warning
                if (isEditDrawingDesignationMode) {
                    return
                }

                // For locked keys not in edit mode, don't activate editor on focus
                // Let the onClick handler determine if we should activate based on where they clicked
                if (isPTKey && !isEditMode) {
                    return
                }

                // For all other cases, activate the editor
                handlers.onFocusCapture(event)
            },
            [isPTKey, isEditMode, isEditDrawingDesignationMode, handlers]
        )

        // Click handler that checks if clicking on locked key portion
        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLElement>) => {
                const target = event.target as HTMLElement
                const canShowWarning = !inactive

                // Check click targets
                const clickedOnDesignationPrefix = target.closest(
                    '[data-testid="designation-prefix"]'
                )
                const clickedOnKeyedText = target.closest(
                    '[data-testid^="keyed-text"]'
                )

                // 1. Designation prefix click (PT key in designation mode)
                if (clickedOnDesignationPrefix && isPTKey && canShowWarning) {
                    event.preventDefault()
                    event.stopPropagation()
                    showPTKeyWarning(keyedText || '')
                    return
                }

                // 2. Drawing designation mode
                if (isEditDrawingDesignationMode) {
                    // Single-element PTs have no separate value PlainText —
                    // the suffix is rendered by DesignationField, so activate
                    // this PlainText's CKEditor when clicking on the suffix area
                    if (!hasValueTextAfterFieldRef) {
                        const clickedOnDesignationSuffix = target.closest(
                            '[data-testid="designation-suffix"]'
                        )
                        if (clickedOnDesignationSuffix) {
                            handlers.onFocusCapture()
                        }
                    }
                    handlers.onClick(event)
                    return
                }

                // 3. PT first items (non-keyed) - entire content is the key
                if (isPTFirstItem && !isKeyedText) {
                    event.preventDefault()
                    event.stopPropagation()
                    if (canShowWarning) {
                        showPTKeyWarning(text || '')
                    }
                    return
                }

                // 4. Locked keyed text - block clicks on key portion
                if (isPTKey && clickedOnKeyedText) {
                    // Let event propagate to KeyedText for its own dialog handling
                    return
                }

                // 5. Locked keys clicking on value - activate editor
                if (isPTKey) {
                    handlers.onFocusCapture()
                }

                handlers.onClick(event)
            },
            [
                isPTKey,
                isPTKey,
                isPTFirstItem,
                isKeyedText,
                text,
                keyedText,
                inactive,
                handlers,
                isEditDrawingDesignationMode,
                hasValueTextAfterFieldRef,
            ]
        )

        // Check if element should be hidden (Drawing Designation feature)
        if (
            shouldHideElement({
                node,
                contentItem,
                isPTFirstItem,
            })
        ) {
            return null
        }

        return (
            <Typography
                variant="caption"
                key={formattedContentId}
                data-testid={formattedContentId}
                data-suggestion={suggestionDataAttribute}
                className={clsx(classes, containerClass)}
                tabIndex={0}
                ref={ckeToggle.active ? ckeToggle.placeholderRef : containerRef}
                onKeyDown={handlers.onKeyDown}
                onKeyUp={handlers.onKeyUp}
                onFocusCapture={handleFocusCapture}
                onClick={handleClick}
                onMouseMove={handlers.onMouseMove}
            >
                {isEditMode ? (
                    <EditorContent
                        initialData={cleanedInitialData}
                        initialSuggestions={initialSuggestions}
                        clickCoordinates={clickCoordinates}
                        handlers={handlers}
                        isKeyedText={isKeyedText}
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        classes={classes}
                        designationProps={{
                            isProductType: node?.isProductType,
                            isArticle: isArticleWithProductType,
                            contentItemId: contentItem.id,
                            nodeId: node?.key,
                            isPTKey,
                            serverDesignation:
                                node?.contentItemView?.drawingDesignation,
                        }}
                    />
                ) : isEditDrawingDesignationMode ? (
                    <DesignationField
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        contentItemId={contentItem?.id || ''}
                        editable={true}
                        inactive={false}
                        isArticle={isArticleWithProductType}
                        showSuffix={
                            !isArticleWithProductType &&
                            !hasValueTextAfterFieldRef
                        }
                        serverDesignation={
                            node?.contentItemView?.drawingDesignation
                        }
                    />
                ) : isPTKey && isKeyedText ? (
                    // For locked keys not in edit mode, render key and value separately
                    // so we can block clicks on the key portion
                    <>
                        <KeyedText
                            keyedText={keyedText}
                            classes={classes}
                            data-testid="keyed-text-key-display"
                            isPTKey={isPTKey}
                        />
                        {keyedTextValue}
                    </>
                ) : (
                    displayText
                )}
                {showEmptyPlainTextContainer && (
                    <EmptyPlainTextContainer
                        element={{
                            id: `${contentItem.id}${ADDITIONAL_ELEMENT_SUFFIX}`,
                            text: '',
                            type: contentItem.type,
                        }}
                        inactive={inactive}
                        editable={true}
                        node={node}
                    />
                )}
            </Typography>
        )
    }
)
